import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Check, Plus } from "lucide-react";
import { NAV_MORE, NAV_PRIMARY, type NavItemData } from "@/lib/nav-items";

import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { AdvaSidebar } from "@/components/layout/adva-sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Kbd } from "@/components/ui/kbd";
import { KeyboardShortcutsProvider } from "@/components/keyboard-shortcuts-provider";
import { cn, getInitials } from "@/lib/utils";
import { SHORTCUTS, getShortcut } from "@/lib/shortcuts";
import {
  useMyBusinesses,
  useSwitchBusiness,
  resolveLogoSrc,
} from "@/hooks/use-businesses";
import { useMyAccounts, useSwitchAccount } from "@/hooks/use-accounts";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useImportPendingReviewCount } from "@/hooks/use-import";
import { useAbility } from "@/hooks/use-ability";
import { useAuth } from "@/contexts/auth-context";
import { useTheme } from "@/contexts/theme-context";
import { BusinessFormSheet } from "@/components/v2/business-form-sheet";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { AddDemoAccount } from "@/components/v2/add-demo-account";
import { DemoBanner } from "@/components/v2/demo-banner";
import { useDeleteDemoAccount } from "@/hooks/use-demo-account";

const NAV_SHORTCUT_BY_HREF: Record<string, string> = Object.fromEntries(
  SHORTCUTS.filter((s) => s.href).map((s) => [s.href!, s.compact]),
);

const STORAGE_KEY = "adva:nav-collapsed-groups";

function useMoreCollapsed() {
  const location = useLocation();
  const moreActive = NAV_MORE.some((item) =>
    location.pathname.startsWith(item.href),
  );

  const [collapsed, setCollapsed] = useState(() => {
    if (moreActive) return false;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const groups = JSON.parse(stored) as string[];
        return groups.includes("more");
      }
    } catch {
      // ignore
    }
    return true;
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const groups: string[] = stored ? JSON.parse(stored) : [];
        if (next) {
          if (!groups.includes("more")) groups.push("more");
        } else {
          const idx = groups.indexOf("more");
          if (idx >= 0) groups.splice(idx, 1);
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
      } catch {
        // ignore
      }
      return next;
    });
  };

  return { collapsed, toggle };
}

function useDynamicBadgeCount(
  source: NavItemData["dynamicBadge"],
): number | null {
  const importPending = useImportPendingReviewCount();
  if (source === "import-pending-review") {
    return importPending.data?.count ?? null;
  }
  return null;
}

function NavItem({ item }: { item: NavItemData }) {
  const location = useLocation();
  const dynamicCount = useDynamicBadgeCount(item.dynamicBadge);
  const isActive =
    item.href === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.href);

  const shortcut = NAV_SHORTCUT_BY_HREF[item.href];

  const badgeCount = item.dynamicBadge ? dynamicCount : item.count;
  // Dynamic badges are hidden when count is 0 (nothing to highlight);
  // static counts always render once provided to preserve existing behavior.
  const showBadge =
    badgeCount != null && (item.dynamicBadge ? badgeCount > 0 : true);

  const button = (
    <SidebarMenuButton
      asChild
      isActive={isActive}
      className={cn(
        "h-8 gap-2 px-3 text-[length:var(--font-size-body)] font-normal",
        isActive
          ? "border-l-2 border-l-[color:var(--color-accent)] bg-sidebar-accent text-sidebar-accent-foreground"
          : "border-l-2 border-l-transparent text-sidebar-muted-foreground hover:text-sidebar-foreground",
      )}
    >
      <NavLink to={item.href}>
        <item.icon className="size-4 shrink-0" strokeWidth={1.5} />
        <span className="truncate">{item.label}</span>
        {showBadge && (
          <SidebarMenuBadge
            className={cn(
              "typo-mono text-[length:var(--font-size-label)]",
              item.dynamicBadge === "import-pending-review"
                ? "text-[color:var(--color-warning)]"
                : "text-sidebar-muted-foreground",
            )}
          >
            {badgeCount}
          </SidebarMenuBadge>
        )}
      </NavLink>
    </SidebarMenuButton>
  );

  return (
    <SidebarMenuItem>
      {shortcut ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            <span>{item.label}</span>
            <Kbd>{shortcut}</Kbd>
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </SidebarMenuItem>
  );
}

function BusinessIdentity() {
  const { data, isLoading } = useMyBusinesses();
  const switchBusiness = useSwitchBusiness();
  const { data: accountData } = useMyAccounts();
  const switchAccount = useSwitchAccount();
  const deleteDemo = useDeleteDemoAccount();
  const { signOut } = useAuth();
  const { sidebarTheme } = useTheme();
  const currentUser = useCurrentUser();
  const ability = useAbility();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteDemoOpen, setDeleteDemoOpen] = useState(false);

  const businesses = data?.businesses ?? [];
  const activeBusinessId = data?.active_business_id;
  const activeBusiness = businesses.find((b) => b.id === activeBusinessId);
  const activeInitials = activeBusiness
    ? getInitials(activeBusiness.name)
    : "?";
  const activeLogoSrc = activeBusiness
    ? resolveLogoSrc(activeBusiness)
    : undefined;

  const accounts = accountData?.accounts ?? [];
  const activeAccountId = accountData?.active_account_id;
  const demoAccount = accounts.find((a) => a.is_demo === true) ?? null;

  const hasMultipleAccounts = accounts.length > 1;
  const canAddBusiness = ability.can("create", "Business");
  const goSettings = getShortcut("goSettings");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-sidebar-accent">
          <Avatar className="size-6 rounded-sm">
            {activeLogoSrc ? <AvatarImage src={activeLogoSrc} alt="" /> : null}
            <AvatarFallback className="rounded-sm bg-[color:var(--color-accent)] text-[10px] font-medium text-white">
              {isLoading ? "…" : activeInitials}
            </AvatarFallback>
          </Avatar>
          <span className="flex-1 truncate text-[length:var(--font-size-body)] font-medium text-sidebar-foreground">
            {isLoading ? "Loading…" : (activeBusiness?.name ?? "—")}
          </span>
          <ChevronDown className="size-4 shrink-0 text-sidebar-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-theme={sidebarTheme}
        className="w-[var(--radix-dropdown-menu-trigger-width)] bg-[color:var(--color-elevated)] border-[color:var(--color-border)] text-[color:var(--color-text-hi)]"
      >
        <DropdownMenuItem
          asChild
          className="focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
        >
          <Link to="/settings">
            <span>Settings</span>
            {goSettings ? (
              <DropdownMenuShortcut>{goSettings.compact}</DropdownMenuShortcut>
            ) : null}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          asChild
          className="focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
        >
          <Link to="/settings/members">
            <span>Invite and manage members</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[color:var(--color-border)]" />

        {/* Switch business ▸ — cascading submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)] data-[state=open]:bg-[color:var(--color-elevated)]">
            <span>Switch business</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent
            data-theme={sidebarTheme}
            className="min-w-[16rem] bg-[color:var(--color-elevated)] border-[color:var(--color-border)] text-[color:var(--color-text-hi)]"
          >
            {hasMultipleAccounts
              ? accounts.map((acc) =>
                  acc.id === activeAccountId ? (
                    // Active account → nested submenu of its businesses
                    <DropdownMenuSub key={acc.id}>
                      <DropdownMenuSubTrigger className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)] data-[state=open]:bg-[color:var(--color-elevated)]">
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{acc.name}</p>
                          <p className="truncate typo-label text-[color:var(--color-text-lo)]">
                            {acc.slug}
                          </p>
                        </div>
                        <Check className="size-4 shrink-0 text-[color:var(--color-accent)]" />
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        data-theme={sidebarTheme}
                        className="min-w-[16rem] bg-[color:var(--color-elevated)] border-[color:var(--color-border)] text-[color:var(--color-text-hi)]"
                      >
                        {businesses.map((biz) => (
                          <DropdownMenuItem
                            key={biz.id}
                            onClick={() => switchBusiness.mutate(biz.id)}
                            disabled={switchBusiness.isPending}
                            className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
                          >
                            <Avatar className="size-6 rounded-sm shrink-0">
                              {resolveLogoSrc(biz) ? (
                                <AvatarImage src={resolveLogoSrc(biz)} alt="" />
                              ) : null}
                              <AvatarFallback className="rounded-sm bg-[color:var(--color-accent)] text-[10px] font-medium text-white">
                                {getInitials(biz.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="truncate">{biz.name}</p>
                              <p className="truncate typo-label text-[color:var(--color-text-lo)]">
                                {biz.slug}
                              </p>
                            </div>
                            {biz.id === activeBusinessId && (
                              <Check className="size-4 shrink-0 text-[color:var(--color-accent)]" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (
                    // Non-active account → flat clickable row that switches
                    // account. Cross-account business listing is not available
                    // from the active-account-keyed /core/me/businesses endpoint;
                    // user re-opens the dropdown after switching to pick a business.
                    <DropdownMenuItem
                      key={acc.id}
                      onClick={() => switchAccount.mutate(acc.id)}
                      disabled={switchAccount.isPending}
                      className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{acc.name}</p>
                        <p className="truncate typo-label text-[color:var(--color-text-lo)]">
                          {acc.slug}
                        </p>
                      </div>
                      {acc.is_demo && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] py-0 px-1.5 h-4 shrink-0"
                        >
                          Demo
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  ),
                )
              : // Single-account: skip the outer account layer, show businesses directly
                businesses.map((biz) => (
                  <DropdownMenuItem
                    key={biz.id}
                    onClick={() => switchBusiness.mutate(biz.id)}
                    disabled={switchBusiness.isPending}
                    className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
                  >
                    <Avatar className="size-6 rounded-sm shrink-0">
                      {resolveLogoSrc(biz) ? (
                        <AvatarImage src={resolveLogoSrc(biz)} alt="" />
                      ) : null}
                      <AvatarFallback className="rounded-sm bg-[color:var(--color-accent)] text-[10px] font-medium text-white">
                        {getInitials(biz.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{biz.name}</p>
                      <p className="truncate typo-label text-[color:var(--color-text-lo)]">
                        {biz.slug}
                      </p>
                    </div>
                    {biz.id === activeBusinessId && (
                      <Check className="size-4 shrink-0 text-[color:var(--color-accent)]" />
                    )}
                  </DropdownMenuItem>
                ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {canAddBusiness && (
          <DropdownMenuItem
            onClick={() => {
              // Defer opening the sheet so the dropdown's auto-close unmount
              // does not race the dialog mount (Radix focus-trap collision).
              setTimeout(() => setAddOpen(true), 0);
            }}
            className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
          >
            <Plus className="size-4 shrink-0" />
            <span>Add business</span>
          </DropdownMenuItem>
        )}

        {demoAccount && demoAccount.id !== activeAccountId && (
          <DropdownMenuItem
            onClick={() => switchAccount.mutate(demoAccount.id)}
            disabled={switchAccount.isPending}
            className="gap-2 focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-text-hi)]"
          >
            <span>Go to demo account</span>
          </DropdownMenuItem>
        )}

        {demoAccount && (
          <DropdownMenuItem
            onClick={() => {
              // Defer opening the AlertDialog so the dropdown's auto-close
              // unmount doesn't race the dialog mount (Radix focus-trap
              // collision — same pattern as "Add business" above). Don't
              // "clean up" the setTimeout: removing it reintroduces the race.
              setTimeout(() => setDeleteDemoOpen(true), 0);
            }}
            className="gap-2 text-[color:var(--color-danger)] focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-danger)]"
          >
            <span>Delete demo account</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className="bg-[color:var(--color-border)]" />

        {/* User identity + Log out grouped as one block at the bottom */}
        <div className="flex items-center gap-2 px-2 py-2">
          <Avatar className="size-6">
            <AvatarFallback className="text-[10px] font-medium">
              {currentUser ? getInitials(currentUser.displayName) : "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 truncate">
            <p className="truncate text-[length:var(--font-size-body)] font-medium">
              {currentUser?.displayName ?? "User"}
            </p>
            <p className="truncate text-[length:var(--font-size-label)] text-[color:var(--color-text-lo)]">
              {currentUser?.email ?? ""}
            </p>
          </div>
        </div>

        <DropdownMenuItem
          onClick={() => void signOut()}
          className="text-[color:var(--color-danger)] focus:bg-[color:var(--color-elevated)] focus:text-[color:var(--color-danger)]"
        >
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
      <BusinessFormSheet open={addOpen} onClose={() => setAddOpen(false)} />
      {demoAccount && (
        <ConfirmDialog
          open={deleteDemoOpen}
          onOpenChange={setDeleteDemoOpen}
          title="Delete demo account"
          description="This will permanently delete the Canopy Home Services demo account and all its data. Your production account is unaffected."
          confirmLabel="Delete demo account"
          variant="destructive"
          pending={deleteDemo.isPending}
          onConfirm={() => {
            deleteDemo.mutate(demoAccount.id, {
              onSuccess: () => setDeleteDemoOpen(false),
            });
          }}
        />
      )}
    </DropdownMenu>
  );
}

export default function AppLayoutV2() {
  return (
    <KeyboardShortcutsProvider>
      <AppLayoutV2Inner />
    </KeyboardShortcutsProvider>
  );
}

function AppLayoutV2Inner() {
  const { collapsed, toggle } = useMoreCollapsed();

  return (
    <SidebarProvider>
      <AdvaSidebar>
        <SidebarHeader className="p-3">
          <BusinessIdentity />
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup className="px-1 py-2">
            <SidebarGroupContent>
              <SidebarMenu>
                {NAV_PRIMARY.map((item) => (
                  <NavItem key={item.label} item={item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup className="px-1 py-0">
            <button
              onClick={toggle}
              className="flex h-8 items-center gap-1 px-3 typo-label text-sidebar-muted-foreground hover:text-sidebar-foreground"
            >
              <ChevronRight
                className={cn(
                  "size-3 transition-transform",
                  !collapsed && "rotate-90",
                )}
              />
              More
            </button>
            {!collapsed && (
              <SidebarGroupContent>
                <SidebarMenu>
                  {NAV_MORE.map((item) => (
                    <NavItem key={item.label} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </SidebarContent>
      </AdvaSidebar>

      {/* min-w-0 (ADV-823): SidebarInset is a row-flex item whose default
          min-width:auto lets wide intrinsic content (e.g. the board's w-72×5
          column rail) blow the inset past the viewport and put a horizontal
          scrollbar on the app shell. Zeroing the minimum keeps the inset at
          viewport width so wide content clips/scrolls in its own rail. */}
      <SidebarInset className="min-w-0 bg-[color:var(--color-bg)]">
        <DemoBanner />
        <main className="min-w-0 flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </SidebarInset>
      <AddDemoAccount />
    </SidebarProvider>
  );
}
